import { z } from "zod";
import { AgentId, AlbertineServiceAgentId, ISODate } from "./primitives";
import { FirebaseTimestamp } from "./record.firestore";

function isTypeOf<S extends z.ZodType>(
    possible: unknown,
    schema: S,
): possible is z.infer<S> {
    return schema.safeParse(possible).success;
}

export const defaultBillingPeriodId = "yearly";
export const defaultMembershipTierId = "founding-tier";
export const essentialMembershipTier = "essential-tier";
export const defaultCommitmentPeriodId = "12-month";
export const defaultTrialPeriodId = "trial-14-days";

export const CommitmentPeriodId = z.enum(["12-month"]);
export type CommitmentPeriodId = z.infer<typeof CommitmentPeriodId>;
export const CommitmentPeriodIdMap: Record<CommitmentPeriodId, string> = {
    "12-month": "12 Months",
};

export const CommitmentPeriod = z.object({
    id: CommitmentPeriodId,
    name: z.string().max(60),
    value: z.number(),
    active: z.boolean(),
});
export type CommitmentPeriod = z.infer<typeof CommitmentPeriod>;

export const defaultCommitmentPeriods: CommitmentPeriod[] = [
    {
        id: defaultCommitmentPeriodId,
        name: "12 months",
        value: 12,
        active: true,
    },
];

export const TrialId = z.enum([
    "trial-14-days",
    "trial-1-month",
    "trial-3-month",
    "no-trial",
]);
export type TrialId = z.infer<typeof TrialId>;

export const MembershipTrial = z.object({
    id: TrialId,
    name: z.string().max(60),
    value: z.number(),
    active: z.boolean(),
});
export type MembershipTrial = z.infer<typeof MembershipTrial>;

export type FilterByMembershipTrial = {
    id: MembershipTierId;
    name: string;
    count: number;
};
export type FilterByCommitmentPeriod = {
    id: CommitmentPeriodId;
    name: string;
    count: number;
};

export const MembershipStageId = z.enum(["trial", "paid"]);
export type MembershipStageId = z.infer<typeof MembershipStageId>;

export const defaultTrialPeriods: MembershipTrial[] = [
    {
        id: "trial-14-days",
        name: "14 days",
        // The value is in 30 day long months but it works with fractional months too.
        // Some floating point rounding error might happen but this works well enough.
        value: 14 / 30,
        active: true,
    },
    {
        id: "trial-1-month",
        name: "1 months",
        value: 1,
        active: true,
    },
    {
        id: "trial-3-month",
        name: "3 months",
        value: 3,
        active: true,
    },
    {
        id: "no-trial",
        name: "No trial",
        value: 0,
        active: true,
    },
];

export function isNoTrial(trialId: TrialId): trialId is "no-trial" {
    return trialId === "no-trial";
}

export const BillingPeriodId = z.enum(["monthly", "yearly"]);
export type BillingPeriodId = z.infer<typeof BillingPeriodId>;

export const BillingPeriodIdMap: Record<BillingPeriodId, string> = {
    monthly: "Monthly",
    yearly: "Yearly",
};

export const TrackedMembershipTierId = z.enum([
    "hugoboss",
    "mastercard",
    "founding-tier",
]);

export const MembershipTierId = TrackedMembershipTierId.or(
    z.string().regex(/^[a-z0-9][-a-z0-9]{0,254}$/, "Invalid membership id."),
);
export type TrackedMembershipTierId = z.infer<typeof TrackedMembershipTierId>;
export type MembershipTierId = z.infer<typeof MembershipTierId>;

export const defaultEndMembershipReason = "other";
export const EndMembershipReason = z.enum([
    "access",
    "price",
    "value",
    "service-level",
    "would-not-use",
    "other",
    "unknown",
]);
export type EndMembershipReason = z.infer<typeof EndMembershipReason>;
export const EndMembershipReasonMap: Record<EndMembershipReason, string> = {
    access: "Access",
    price: "Price",
    value: "Value",
    "service-level": "Service Level",
    "would-not-use": "Wouldn't Use",
    other: "Other",
    unknown: "Unknown",
};

export const MembershipCancellation = z.object({
    reason: EndMembershipReason,
    additionalNote: z.string().nullable().optional(),
    cancelledAt: FirebaseTimestamp.optional().nullable(),
    cancelledBy: AgentId.or(AlbertineServiceAgentId),
});
export type MembershipCancellation = z.infer<typeof MembershipCancellation>;

export const MembershipCommon = z.object({
    tier: MembershipTierId,
    cancellation: MembershipCancellation.optional().nullable(),
    /* Represents number of days for which membership is extended */
    extendedFor: z.number().optional().nullable(),
});

export const MembershipStageTrial = MembershipCommon.extend(
    z.object({
        trial: z.enum(["trial-14-days", "trial-1-month", "trial-3-month"]),
        startTrialDate: ISODate.optional().nullable(),
        endTrialDate: ISODate.optional().nullable(),
    }).shape,
);
export type MembershipStageTrial = z.infer<typeof MembershipStageTrial>;

export const MembershipStagePaid = MembershipCommon.extend(
    z.object({
        trial: z.enum(["no-trial"]),
        startDate: ISODate,
        endDate: ISODate,
        commitmentPeriod: CommitmentPeriodId.optional().nullable(),
        billingPeriod: BillingPeriodId.optional().nullable(),
    }).shape,
);
export type MembershipStagePaid = z.infer<typeof MembershipStagePaid>;

export const Membership = z.discriminatedUnion("trial", [
    z
        .object({
            trial: z.literal("trial-14-days"),
        })
        .extend(MembershipStageTrial.omit({ trial: true }).shape),
    z
        .object({
            trial: z.literal("trial-1-month"),
        })
        .extend(MembershipStageTrial.omit({ trial: true }).shape),
    z
        .object({
            trial: z.literal("trial-3-month"),
        })
        .extend(MembershipStageTrial.omit({ trial: true }).shape),
    z
        .object({
            trial: z.literal("no-trial"),
        })
        .extend(MembershipStagePaid.omit({ trial: true }).shape),
]);
export type Membership = z.infer<typeof Membership>;

export const MembershipStagePaidExposedToMember = z.object({
    endDate: ISODate,
    isMembershipCancelled: z.boolean().or(z.undefined()),
});
export type MembershipStagePaidExposedToMember = z.infer<
    typeof MembershipStagePaidExposedToMember
>;

export const TimestampUpdatables = z.object({
    createdAt: FirebaseTimestamp.nullable(),
    updatedAt: FirebaseTimestamp.optional().nullable(),
});

export const MembershipDoc = z.union([Membership, TimestampUpdatables]);
export type MembershipDoc = z.infer<typeof MembershipDoc>;

export function getEndDate(membership: Membership): string | null | undefined {
    if (isTypeOf(membership, MembershipStagePaid)) return membership.endDate;
    return membership.endTrialDate;
}
